import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from '@capacitor/core';
import {isMobileApp} from '@common/core/platform-detection';

const PLUGIN_NAME = 'CapacitorShake';
const noop = () => {};

interface ShakePlugin {
  addListener(
    eventName: 'shake',
    listenerFunc: () => void,
  ): Promise<PluginListenerHandle>;
  getPluginVersion(): Promise<{version: string}>;
}

const CapacitorShake = registerPlugin<ShakePlugin>(PLUGIN_NAME);

class AppShakeManager {
  isSupported(): boolean {
    return (
      isMobileApp() &&
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable(PLUGIN_NAME)
    );
  }

  async addShakeListener(listener: () => void): Promise<() => void> {
    if (!this.isSupported()) {
      return noop;
    }

    try {
      const handle = await CapacitorShake.addListener('shake', listener);
      return () => {
        void handle.remove();
      };
    } catch (error) {
      console.warn('[AppShake] Failed to add shake listener', error);
      return noop;
    }
  }

  async getPluginVersion(): Promise<string | null> {
    if (!this.isSupported()) {
      return null;
    }

    try {
      const result = await CapacitorShake.getPluginVersion();
      return result.version ?? null;
    } catch (error) {
      console.warn('[AppShake] Failed to get plugin version', error);
      return null;
    }
  }
}

export const useAppShakeManager = new AppShakeManager();
